import {Component, HostListener, OnInit, signal, ViewChild} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {AuthService} from '../../../core/services/auth/auth.service';
import {Event, NavigationEnd, Router} from '@angular/router';
import {ToastService} from '../../../core/services/toast/toast.service';
import {SettingsService} from '../../../core/services/global/settings.service';
import {LoginComponent} from 'src/app/modules/user/login/login.component';

@Component({
    selector: 'qa-navigation-bar-header',
    templateUrl: './navigation-bar.component.html',
    styleUrls: ['./navigation-bar.component.scss'],
    standalone: false
})
export class NavigationBarComponent implements OnInit {

  isMaintenancePossible = false;
  potentialMaintenanceDescription = '';

  items: MenuItem[];
  homeMenuItem: MenuItem = {
    label: 'Home',
    icon: 'pi pi-fw pi-home',
    routerLink: ['home'],
    command: () => this.setPreviousUrl('home')
  };
  validateMenuItem: MenuItem = {
    label: 'Validate',
    icon: 'pi pi-fw pi-wrench',
    routerLink: ['validate'],
    command: () => this.setPreviousUrl('validate')
  };
  myResultsMenuItem: MenuItem = {
    label: 'My validations',
    icon: 'pi pi-fw pi-folder',
    routerLink: ['my-validations']
  };
  publishedResultsMenuItem: MenuItem = {
    label: 'Published validations',
    icon: 'pi pi-fw pi-globe',
    routerLink: ['published-validations']
  };
  reportsMenuItem: MenuItem = {
    label: 'Reports',
    icon: 'pi pi-fw pi-file-pdf',
    routerLink: ['reports']
  };
  resultsMenuItem: MenuItem = {
    label: 'Results',
    icon: 'pi pi-fw pi-folder',
    items: [
      this.myResultsMenuItem,
      this.publishedResultsMenuItem,
      this.reportsMenuItem,
    ]
  };
  compareResultsMenuItem: MenuItem = {
    label: 'Compare validations',
    icon: 'pi pi-fw pi-th-large',
    routerLink: ['comparison'],
  };
  uploadDataMenuItem: MenuItem = {
    label: 'My datasets',
    icon: 'pi pi-fw pi-upload',
    routerLink: ['my-datasets'],
  };

  uploadDataHelpMenuItem: MenuItem = {
    label: 'Upload help',
    icon: 'pi pi-fw pi-question',
    routerLink: ['user-data-guidelines']
  };

  datasetUploadMenuItem: MenuItem = {
    label: 'Dataset upload',
    icon: 'pi pi-fw pi-upload',
    items: [
      this.uploadDataMenuItem,
      this.uploadDataHelpMenuItem
    ]
  };

  APIMenuItem: MenuItem = {
    label: 'Public API',
    icon: 'pi pi-fw pi-globe',
    routerLink: ['public-api']
  };

  helpMenuItem: MenuItem = {label: 'Help', icon: 'pi pi-fw pi-question', routerLink: ['help']};
  userManualMenuItem: MenuItem = {label: 'User Manual', icon: 'pi pi-fw pi-book', url: '/api/user-manual', target: '_blank'}
  datasetMenuItem: MenuItem = {label: 'Datasets', icon: 'pi pi-fw pi-database', routerLink: ['datasets']};
  termsMenuItem: MenuItem = {label: 'Terms', icon: 'pi pi-fw pi-briefcase', routerLink: ['terms']};
  customisePlotsMenuItem: MenuItem = {label: 'Custom Plots', icon: 'pi pi-fw pi-chart-line', routerLink: ['customise-plots']};
  
  infoMenuItem: MenuItem = {
    label: 'Info', icon: 'pi pi-fw pi-info-circle', items: [
      this.helpMenuItem,
      this.userManualMenuItem,
      this.customisePlotsMenuItem,
      this.datasetMenuItem,
      this.termsMenuItem,
    ]
  };
  contactMenuItem: MenuItem = {label: 'Contact Us', icon: 'pi pi-fw pi-envelope', routerLink: ['contact-us']};

  loginMenuItem: MenuItem = {
    label: 'Log in',
    icon: 'pi pi-fw pi-sign-in',
    command: () => this.authService.switchLoginModal(true)
  };

  logoutMenuItem: MenuItem = {label: 'Log out', icon: 'pi pi-fw pi-sign-out', command: () => this.logout()};
  userProfileMenuItem: MenuItem = {label: 'User profile', icon: 'pi pi-fw pi-user', routerLink: ['user-profile']};

  userMenuItem: MenuItem = {
    label: `My account`,
    icon: 'pi pi-fw pi-user',
    items: [
      this.userProfileMenuItem,
      this.logoutMenuItem,
    ],
  };

  isAuthenticated: boolean;

  Logo = {path:  '/static/images/logo/qa4sm_logo_long.webp', height: '45', shape: 'rectangle'}

  showLoginModal = false;
  @ViewChild(LoginComponent) loginComponent: LoginComponent;

   constructor(
    private authService: AuthService,
    public router: Router,
    private toastService: ToastService,
    private settingsService: SettingsService
  ) {

    this.authService.showLoginModal$.subscribe(
      (state: { show: boolean; message?: string }) => (this.showLoginModal = state.show)
    );
  }

  ngOnInit(): void {
    this.authService.authenticated.subscribe(authenticated => this.authStatusChanged(authenticated));
    this.setSettingsValues();
  }

  onLoginSuccess() {
    // Hide the login modal window on successful login
    this.authService.switchLoginModal(false);
  }

  private authStatusChanged(authenticated: boolean): void {
    this.loginMenuItem.visible = !authenticated;
    this.userMenuItem.visible = authenticated;
    this.myResultsMenuItem.visible = authenticated;
    this.compareResultsMenuItem.visible = authenticated;
    this.uploadDataMenuItem.visible = authenticated;
    this.setMenuItems();
  }


  private setMenuItems(): void {
    this.items = [
      this.homeMenuItem,
      this.validateMenuItem,
      this.resultsMenuItem,
      this.datasetUploadMenuItem,
      this.compareResultsMenuItem,
      this.APIMenuItem,
      this.infoMenuItem,
      this.contactMenuItem,
      this.loginMenuItem,
      this.userMenuItem,
    ];
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.setPreviousUrl('');
        this.toastService.showSuccessWithHeader('Logout', 'Successful logout');
      },
      error: () => {
        this.toastService.showErrorWithHeader(
          'Something went wrong.',
          'We could not log you out. Please try again or contact our support team.'
        );
      }
    });
  }

  setPreviousUrl(prevUrl: string): void {
    this.authService.setPreviousUrl(prevUrl);
  }

  setSettingsValues(): void {
    this.settingsService.getAllSettings().subscribe(data => {
      this.isMaintenancePossible = data[0].potential_maintenance;
      this.potentialMaintenanceDescription = data[0].potential_maintenance_description;
    });
  }

  protected readonly window = window;
}
